import DeviceImport from "./DeviceImport";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Smartphone,
  Search,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Wrench,
  Settings,
  History
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  AVAILABLE: { label: "Verfügbar", color: "bg-emerald-500", icon: CheckCircle },
  LOANED: { label: "Ausgeliehen", color: "bg-red-500", icon: XCircle },
  DEFECT: { label: "Defekt", color: "bg-amber-500", icon: Wrench },
  MAINTENANCE: { label: "Wartung", color: "bg-blue-500", icon: Settings }
};

export default function DeviceManagement() {
  const [handhelds, setHandhelds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // Selected device
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceHistory, setDeviceHistory] = useState([]);
  
  // Forms
  const [createForm, setCreateForm] = useState({
    serial_number: "",
    display_name: "",
    model: "Zebra MC94",
    mac_address: "",
    ip_address: ""
  });
  const [editForm, setEditForm] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  const fetchHandhelds = useCallback(async () => {
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : "";
      const response = await axios.get(`${API}/handhelds${params}`);
      setHandhelds(response.data);
    } catch (error) {
      console.error("Error fetching handhelds:", error);
      toast.error("Fehler beim Laden der Geräte");
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchHandhelds();
  }, [fetchHandhelds]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchHandhelds();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchHandhelds]);

  const fetchDeviceHistory = async (deviceId) => {
    try {
      const response = await axios.get(`${API}/handhelds/${deviceId}/history`);
      setDeviceHistory(response.data.entries || []);
    } catch (error) {
      console.error("Error fetching history:", error);
      setDeviceHistory([]);
    }
  };

  const handleCreate = async () => {
    if (!createForm.serial_number || !createForm.display_name) {
      toast.error("Seriennummer und Anzeigename sind erforderlich");
      return;
    }

    setFormLoading(true);
    try {
      await axios.post(`${API}/handhelds`, createForm);
      toast.success("Gerät erfolgreich angelegt");
      setShowCreateModal(false);
      setCreateForm({
        serial_number: "",
        display_name: "",
        model: "Zebra MC94",
        mac_address: "",
        ip_address: ""
      });
      fetchHandhelds();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Anlegen";
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async () => {
    setFormLoading(true);
    try {
      await axios.put(`${API}/handhelds/${selectedDevice.id}`, editForm);
      toast.success("Gerät erfolgreich aktualisiert");
      setShowEditModal(false);
      fetchHandhelds();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Aktualisieren";
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/handhelds/${selectedDevice.id}`);
      toast.success("Gerät erfolgreich gelöscht");
      setShowDeleteDialog(false);
      setSelectedDevice(null);
      fetchHandhelds();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Löschen";
      toast.error(message);
    }
  };

  const openEditModal = (device) => {
    setSelectedDevice(device);
    setEditForm({
      display_name: device.display_name,
      model: device.model,
      status: device.status,
      mac_address: device.mac_address || "",
      ip_address: device.ip_address || ""
    });
    setShowEditModal(true);
  };

  const openHistoryModal = async (device) => {
    setSelectedDevice(device);
    await fetchDeviceHistory(device.id);
    setShowHistoryModal(true);
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="device-management">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Zurück
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-xl font-bold text-slate-900">
                  Geräteverwaltung
                </h1>
                <p className="text-sm text-slate-500">Zebra MC94 Handhelds verwalten</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Suche..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <DeviceImport onImported={fetchHandhelds} />
              <Button onClick={() => setShowCreateModal(true)} data-testid="create-device-btn">
                <Plus className="h-4 w-4 mr-2" />
                Neues Gerät
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Geräteliste
              <Badge variant="outline" className="ml-2">{handhelds.length} Geräte</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Anzeigename</TableHead>
                    <TableHead>Seriennummer</TableHead>
                    <TableHead>Modell</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>MAC-Adresse</TableHead>
                    <TableHead>IP-Adresse</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {handhelds.map((device) => {
                    const config = STATUS_CONFIG[device.status] || STATUS_CONFIG.AVAILABLE;
                    return (
                      <TableRow key={device.id}>
                        <TableCell className="font-mono font-medium">{device.display_name}</TableCell>
                        <TableCell className="font-mono text-slate-500">{device.serial_number}</TableCell>
                        <TableCell>{device.model}</TableCell>
                        <TableCell>
                          <Badge className={config.color}>{config.label}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{device.mac_address || "-"}</TableCell>
                        <TableCell className="font-mono text-sm">{device.ip_address || "-"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openHistoryModal(device)}
                              title="Historie"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(device)}
                              title="Bearbeiten"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedDevice(device);
                                setShowDeleteDialog(true);
                              }}
                              title="Löschen"
                              disabled={device.status === "LOANED"}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Neues Gerät anlegen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Seriennummer *</Label>
              <Input
                value={createForm.serial_number}
                onChange={(e) => setCreateForm({...createForm, serial_number: e.target.value})}
                placeholder="Seriennummer vom Chip"
                className="font-mono"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Anzeigename *</Label>
              <Input
                value={createForm.display_name}
                onChange={(e) => setCreateForm({...createForm, display_name: e.target.value})}
                placeholder="z.B. 300001"
                className="font-mono"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Modell</Label>
              <Input
                value={createForm.model}
                onChange={(e) => setCreateForm({...createForm, model: e.target.value})}
                placeholder="Zebra MC94"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>MAC-Adresse</Label>
                <Input
                  value={createForm.mac_address}
                  onChange={(e) => setCreateForm({...createForm, mac_address: e.target.value})}
                  placeholder="00:00:00:00:00:00"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>IP-Adresse</Label>
                <Input
                  value={createForm.ip_address}
                  onChange={(e) => setCreateForm({...createForm, ip_address: e.target.value})}
                  placeholder="192.168.1.1"
                  className="font-mono"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreate} disabled={formLoading}>
              {formLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                "Anlegen"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Gerät bearbeiten
            </DialogTitle>
            <DialogDescription>
              Seriennummer: {selectedDevice?.serial_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Anzeigename</Label>
              <Input
                value={editForm.display_name || ""}
                onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
                className="font-mono"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Modell</Label>
              <Input
                value={editForm.model || ""}
                onChange={(e) => setEditForm({...editForm, model: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(value) => setEditForm({...editForm, status: value})}
                disabled={selectedDevice?.status === "LOANED"}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AVAILABLE">Verfügbar</SelectItem>
                  <SelectItem value="DEFECT">Defekt</SelectItem>
                  <SelectItem value="MAINTENANCE">Wartung</SelectItem>
                </SelectContent>
              </Select>
              {selectedDevice?.status === "LOANED" && (
                <p className="text-xs text-amber-600">Status kann nur durch Rückgabe geändert werden</p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>MAC-Adresse</Label>
                <Input
                  value={editForm.mac_address || ""}
                  onChange={(e) => setEditForm({...editForm, mac_address: e.target.value})}
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>IP-Adresse</Label>
                <Input
                  value={editForm.ip_address || ""}
                  onChange={(e) => setEditForm({...editForm, ip_address: e.target.value})}
                  className="font-mono"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleEdit} disabled={formLoading}>
              {formLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                "Speichern"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Ausleihhistorie - {selectedDevice?.display_name}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="h-80">
            {deviceHistory.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Keine Historie vorhanden</p>
            ) : (
              <div className="space-y-2">
                {deviceHistory.map((loan) => (
                  <div key={loan.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <Badge className={loan.status === "OPEN" ? "bg-red-500" : "bg-emerald-500"}>
                        {loan.status === "OPEN" ? "Offen" : "Abgeschlossen"}
                      </Badge>
                      <span className="text-xs text-slate-500">{loan.shift}</span>
                    </div>
                    <div className="text-sm font-medium">{loan.user_name}</div>
                    <div className="text-xs text-slate-500">{loan.user_company} • {loan.user_personal_number}</div>
                    <div className="mt-2 text-xs text-slate-500">
                      <div>Ausgabe: {formatDate(loan.start_timestamp)} ({loan.issued_by_staff_name})</div>
                      {loan.end_timestamp && (
                        <div>Rückgabe: {formatDate(loan.end_timestamp)} ({loan.returned_by_staff_name})</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryModal(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gerät löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie das Gerät <strong>{selectedDevice?.display_name}</strong> wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

